import { faHouse, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { showSuccessThunk } from '@spike/notifications-action';
import {
    clearServiceSessionsWarnings,
    saveDefaultSessionsBatchThunk
} from 'actions/serviceSessions/ServiceSessionsActions';
import Button from 'components/UI/V2/Button/Button';
import { Select } from 'components/UI/V2/Forms/Select/Select';
import { WeekDay } from 'model';
import { Service } from 'model/Service';
import Staff from 'model/Staff';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    duplicateAssignmentKeys,
    getServiceSessionConfig,
    getWeeklySessionsForWeekDay,
    isSameSessionAssignment,
    SessionZone,
    weekDayIndex,
    WEEK_DAYS,
    WEEK_DAYS_FROM_MONDAY,
    SESSION_DUPLICATE_SLOT_MESSAGE,
    SESSION_ZONE_REQUIRED_MESSAGE,
    SessionBusinessArea
} from 'model/ServiceSession';
import { MultiSlotDatePicker, MultiSlotDatePickerOption } from '../MultiSlotDrawer/DatePicker';
import { MultiSlotDrawerBody } from '../MultiSlotDrawer/MultiSlotDrawerBody';
import { MultiSlotDrawerFooter } from '../MultiSlotDrawer/MultiSlotDrawerFooter';
import { MultiSlotDrawerHeader } from '../MultiSlotDrawer/MultiSlotDrawerHeader';
import { useSessionHourOptions } from './useSessionHourOptions';
import { SessionConflictWarnings } from './SessionConflictWarnings';
import { SessionStaffSlotRow, SessionStaffSlots } from './SessionStaffSlotRow';
import { ZoneSelectionPanel } from './ZoneSelectionPanel';

export interface SessionSetupDefaultDrawerProps {
    area: SessionBusinessArea;
    staff: Array<Staff>;
    onZonePanelChange: (open: boolean) => void;
    services: Array<Service>;
    onClose: () => void;
}

interface DraftAssignment {
    key: string;
    staffId?: number;
    time?: string;
    zone?: SessionZone;
}

type DraftAssignmentsByWeekDay = Record<WeekDay, Array<DraftAssignment>>;

const DAY_TAB_OPTIONS: Array<MultiSlotDatePickerOption<WeekDay>> = [
    { label: 'Mon', value: 'monday' },
    { label: 'Tue', value: 'tuesday' },
    { label: 'Wed', value: 'wednesday' },
    { label: 'Thu', value: 'thursday' },
    { label: 'Fri', value: 'friday' },
    { label: 'Sat', value: 'saturday' },
    { label: 'Sun', value: 'sunday' }
];

const useStyles = makeStyles(() =>
    createStyles({
        serviceSelect: {
            'marginBottom': 24,

            '& .MuiSelect-root': {
                paddingRight: 40
            }
        },
        serviceValue: {
            gap: 8,
            width: '100%',
            display: 'flex',
            minWidth: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box'
        },
        serviceName: {
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
        },
        homeChip: {
            gap: 5,
            height: 32,
            fontSize: 13,
            flexShrink: 0,
            fontWeight: 500,
            display: 'flex',
            lineHeight: '24px',
            padding: '4px 11px',
            borderRadius: 60,
            alignItems: 'center',
            boxSizing: 'border-box',
            backgroundColor: '#E4DDD5'
        },
        addToTitle: {
            fontSize: 16,
            fontWeight: 600
        },
        daysPicker: {
            '&&': {
                margin: 0,
                paddingTop: 16,
                paddingLeft: 0,
                paddingRight: 0
            }
        },
        addStaffButton: {
            alignSelf: 'flex-start'
        },
        emptyMessage: {
            fontSize: 15,
            color: '#7A7A7A',
            paddingBottom: 8
        }
    })
);

let draftKey = 0;
const newAssignment = (): DraftAssignment => ({ key: `assignment-${draftKey++}`, time: '09:00' });

const createDraftAssignments = (
    days: RootState['serviceSessions']['days'],
    serviceId?: number
): DraftAssignmentsByWeekDay => {
    return WEEK_DAYS_FROM_MONDAY.reduce((drafts, day) => {
        const saved = serviceId ? getWeeklySessionsForWeekDay(days, serviceId, day) : [];
        drafts[day] =
            saved.length > 0
                ? saved.map(session => ({
                      key: session.uuid,
                      staffId: session.staffId,
                      time: session.time,
                      zone: session.zone
                  }))
                : [newAssignment()];
        return drafts;
    }, {} as DraftAssignmentsByWeekDay);
};

export const SessionSetupDefaultDrawer: React.FC<SessionSetupDefaultDrawerProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const days = useSelector((state: RootState) => state.serviceSessions.days);
    const saving = useSelector((state: RootState) => state.serviceSessions.saving);
    const warnings = useSelector((state: RootState) => state.serviceSessions.warnings);
    const assignmentErrors = useSelector((state: RootState) => state.serviceSessions.assignmentErrors);

    const [serviceId, setServiceId] = React.useState<number>();
    const [weekDay, setWeekDay] = React.useState<WeekDay>('monday');
    const [assignmentsByWeekDay, setAssignmentsByWeekDay] = React.useState<DraftAssignmentsByWeekDay>(() =>
        createDraftAssignments([])
    );
    const [dirtyWeekDays, setDirtyWeekDays] = React.useState<Array<WeekDay>>([]);
    const [editingZoneKey, setEditingZoneKey] = React.useState<string>();
    const [showZoneErrors, setShowZoneErrors] = React.useState(false);

    const service = React.useMemo(
        () => props.services.find(item => item.id === serviceId),
        [props.services, serviceId]
    );

    React.useEffect(() => {
        dispatch(clearServiceSessionsWarnings());
    }, []);

    const config = service ? getServiceSessionConfig(service, props.area) : undefined;
    const hourOptions = useSessionHourOptions();
    const assignments = assignmentsByWeekDay[weekDay];

    React.useEffect(() => {
        setAssignmentsByWeekDay(createDraftAssignments(days, serviceId));
        setDirtyWeekDays([]);
        setShowZoneErrors(false);
    }, [serviceId, days]);

    const updateAssignments = (update: (current: Array<DraftAssignment>) => Array<DraftAssignment>) => {
        dispatch(clearServiceSessionsWarnings());
        setShowZoneErrors(false);
        setAssignmentsByWeekDay(current => ({
            ...current,
            [weekDay]: update(current[weekDay])
        }));
        setDirtyWeekDays(current => (current.includes(weekDay) ? current : [...current, weekDay]));
    };

    const changeAssignmentHandler = (key: string, values: { staffId?: number; time?: string }) => {
        updateAssignments(current => current.map(item => (item.key === key ? { ...item, ...values } : item)));
    };

    const editZoneHandler = (key?: string) => {
        setEditingZoneKey(key);
        props.onZonePanelChange(!!key);
    };

    const saveZoneHandler = (zone: SessionZone) => {
        updateAssignments(current => current.map(item => (item.key === editingZoneKey ? { ...item, zone } : item)));
        editZoneHandler(undefined);
    };

    const removeZoneHandler = (key: string) => {
        updateAssignments(current => current.map(item => (item.key === key ? { ...item, zone: undefined } : item)));
    };

    const completedAssignments = assignments.filter(item => item.staffId && item.time);
    const duplicateKeys = React.useMemo(() => duplicateAssignmentKeys(assignments), [assignments]);
    const canSave =
        !!serviceId &&
        dirtyWeekDays.length > 0 &&
        dirtyWeekDays.every(
            day =>
                assignmentsByWeekDay[day].every(assignment => !!assignment.staffId && !!assignment.time) &&
                duplicateAssignmentKeys(assignmentsByWeekDay[day]).size === 0
        );

    const errorFor = (key: string, staffId?: number, time?: string, zone?: SessionZone): string | undefined => {
        if (duplicateKeys.has(key)) return SESSION_DUPLICATE_SLOT_MESSAGE;
        if (!staffId || !time) return undefined;
        if (showZoneErrors && config?.inHome && !zone) return SESSION_ZONE_REQUIRED_MESSAGE;
        return assignmentErrors.find(error => isSameSessionAssignment(error, { staffId, time }))?.messages[0];
    };

    const saveHandler = () => {
        if (!canSave || !serviceId) return;

        const missingZoneWeekDay = config?.inHome
            ? dirtyWeekDays.find(day =>
                  assignmentsByWeekDay[day].some(
                      assignment => assignment.staffId && assignment.time && !assignment.zone
                  )
              )
            : undefined;

        if (missingZoneWeekDay) {
            dispatch(clearServiceSessionsWarnings());
            setWeekDay(missingZoneWeekDay);
            setShowZoneErrors(true);
            return;
        }

        dispatch(
            saveDefaultSessionsBatchThunk(
                serviceId,
                dirtyWeekDays.map(day => ({
                    weekday: weekDayIndex(day),
                    assignments: assignmentsByWeekDay[day].map(item => ({
                        staffId: item.staffId!,
                        time: item.time!,
                        zone: item.zone
                    }))
                })),
                saveWarnings => {
                    setDirtyWeekDays([]);
                    dispatch(
                        showSuccessThunk(
                            saveWarnings.length > 0
                                ? 'Default sessions updated with warnings'
                                : 'Default sessions updated successfully'
                        )
                    );
                    if (saveWarnings.length === 0) props.onClose();
                },
                failedWeekday => {
                    setWeekDay(WEEK_DAYS[failedWeekday]);
                }
            )
        );
    };

    const selectedServiceValue = (value: unknown) => {
        const selected = props.services.find(item => `${item.id}` === value);
        if (!selected) return '';

        const inHome = getServiceSessionConfig(selected, props.area).inHome;

        return (
            <div className={classes.serviceValue}>
                <span className={classes.serviceName}>{selected.name}</span>
                {inHome && (
                    <span className={classes.homeChip}>
                        <FontAwesomeIcon icon={faHouse} />
                        Home Service
                    </span>
                )}
            </div>
        );
    };

    return (
        <>
            <MultiSlotDrawerHeader
                title="Setup Default"
                description="These setup will repeat every week unless changes are made for specific dates."
                onBack={props.onClose}
                onClose={props.onClose}
            >
                <Select<Service>
                    label="Service"
                    placeholder="Select Service"
                    wrapperClassName={classes.serviceSelect}
                    value={serviceId ? `${serviceId}` : ''}
                    options={props.services.map(item => ({
                        value: `${item.id}`,
                        label: item.name,
                        data: item
                    }))}
                    selectProps={{ renderValue: selectedServiceValue }}
                    onChange={event => {
                        dispatch(clearServiceSessionsWarnings());
                        setShowZoneErrors(false);
                        setServiceId(Number(event.target.value));
                    }}
                />

                <Typography className={classes.addToTitle}>Add To</Typography>
                <MultiSlotDatePicker<WeekDay>
                    disabled={!serviceId}
                    options={DAY_TAB_OPTIONS}
                    selectedOption={serviceId ? weekDay : undefined}
                    className={classes.daysPicker}
                    onDateChange={value => {
                        if (!serviceId) return;
                        dispatch(clearServiceSessionsWarnings());
                        setWeekDay(value);
                    }}
                />
            </MultiSlotDrawerHeader>

            <MultiSlotDrawerBody>
                {serviceId && (
                    <>
                        {assignments.length > 0 ? (
                            <SessionStaffSlots showZone={!!config?.inHome}>
                                {assignments.map(assignment => (
                                    <SessionStaffSlotRow
                                        key={assignment.key}
                                        area={props.area}
                                        staff={props.staff}
                                        hours={hourOptions}
                                        time={assignment.time}
                                        zone={assignment.zone}
                                        staffId={assignment.staffId}
                                        showZone={!!config?.inHome}
                                        error={errorFor(
                                            assignment.key,
                                            assignment.staffId,
                                            assignment.time,
                                            assignment.zone
                                        )}
                                        onEditZone={() => editZoneHandler(assignment.key)}
                                        onRemoveZone={() => removeZoneHandler(assignment.key)}
                                        onChange={values => changeAssignmentHandler(assignment.key, values)}
                                        onDelete={() =>
                                            updateAssignments(current =>
                                                current.filter(item => item.key !== assignment.key)
                                            )
                                        }
                                    />
                                ))}
                            </SessionStaffSlots>
                        ) : (
                            <Typography className={classes.emptyMessage}>
                                No sessions on {DAY_TAB_OPTIONS.find(day => day.value === weekDay)?.label}. Save to
                                clear this day, or add a staff member.
                            </Typography>
                        )}

                        <SessionConflictWarnings
                            staff={props.staff}
                            weekDays={[weekDay]}
                            extraWarnings={warnings}
                            assignments={completedAssignments}
                        />

                        <Button
                            variant="primary"
                            label="Add Staff"
                            leftIcon={faPlus}
                            id={`${props.area}-default-add-staff-button`}
                            className={classes.addStaffButton}
                            onClick={() => updateAssignments(current => [...current, newAssignment()])}
                        />
                    </>
                )}
            </MultiSlotDrawerBody>

            <MultiSlotDrawerFooter>
                <Button
                    noColorOnDisabled
                    variant="green"
                    label="Save"
                    disabled={!canSave || saving}
                    isLoading={saving}
                    id={`${props.area}-default-save-button`}
                    onClick={saveHandler}
                />
            </MultiSlotDrawerFooter>

            <ZoneSelectionPanel
                area={props.area}
                open={!!editingZoneKey}
                zone={assignments.find(item => item.key === editingZoneKey)?.zone}
                onSave={saveZoneHandler}
                onClose={() => editZoneHandler(undefined)}
            />
        </>
    );
};
